import { useState, useCallback } from 'react';
import { message } from 'antd';
import { useBanners } from '../../../../context/banners';
import { useFormContext } from '../../../../context/form';
import { AnyCreativityConfig, CreativityType } from '../../../../types/banner';
import { MenuSections } from '../../../../types/menu-sections';
import { getCreativityForms } from '../../../../constants/clients/registry';
import { useCreateFormBanner } from './use-create-form-banner';
import { getClientHandler } from '../../../../handlers';
import { useClientId } from '../../../use-client-id';
import { validateVideoUrl, validateRequiredFields, isVideoUrlField } from '../../../../utils';
import { useModal } from '../../../../context/modal';

export const useFormState = (creativityType: CreativityType, onNavigate: (section: MenuSections) => void) => {
  const { refreshAllBanners } = useBanners();
  const { createFormBanner } = useCreateFormBanner();
  const { setCreativityType } = useFormContext();
  const { openAsset } = useModal();

  const clientId = useClientId();
  const clientHandler = getClientHandler(clientId);

  const creativityForms = getCreativityForms(clientId);
  const formConfig = creativityForms[creativityType];

  const getInitialFormData = useCallback(() => {
    if (!formConfig) return {};
    const defaults: Record<string, any> = {};
    formConfig.fields.forEach(field => {
      if ('defaultValue' in field && field.defaultValue !== undefined) {
        defaults[field.key] = field.defaultValue;
      }
    });
    return defaults;
  }, [formConfig]);

  const [formData, setFormData] = useState(getInitialFormData);
  const [isCreating, setIsCreating] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setFieldError = useCallback((fieldKey: string, error?: string) => {
    setErrors(prev => {
      const next = { ...prev };
      if (error && error.trim()) {
        next[fieldKey] = error;
      } else {
        delete next[fieldKey];
      }
      return next;
    });
  }, []);

  const initializeCreativityType = useCallback(() => {
    setCreativityType(creativityType);
  }, [creativityType, setCreativityType]);

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};
    const fields = formConfig.fields as any[];

    const requiredFields = fields.filter(field => field.required).map(field => field.key);
    const missingFields = validateRequiredFields(formData, requiredFields);

    missingFields.forEach(field => {
      const fieldConfig = fields.find(f => f.key === field);
      newErrors[field] = `${fieldConfig?.label || 'Este campo'} es requerido`;
    });

    Object.entries(formData).forEach(([key, value]) => {
      if (isVideoUrlField(value)) {
        const validation = validateVideoUrl(value.originalUrl);
        if (!validation.isValid) {
          newErrors[key] = 'Debe ser una URL válida (.mp4) o de Instagram/TikTok';
        }
      }
    });

    if (clientHandler.validateForm) {
      const clientErrors = clientHandler.validateForm(formData, newErrors);
      Object.assign(newErrors, clientErrors);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formConfig, formData, clientHandler]);

  const handleFieldChange = useCallback(
    (fieldKey: string, value: any) => {
      setFormData(prev => {
        const next = { ...prev, [fieldKey]: value };

        if (clientHandler.applyFieldLogic) {
          const updates = clientHandler.applyFieldLogic(fieldKey, value, {
            ...next,
            _creativityType: creativityType
          });
          Object.assign(next, updates);
        }
        return next;
      });

      setErrors(prev => {
        if (!prev[fieldKey]) return prev;
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
    },
    [creativityType, clientHandler]
  );

  const handleCreateBanner = useCallback(async () => {
    if (!validateForm()) {
      message.error('Por favor, corrige los errores del formulario');
      return;
    }

    setIsCreating(true);

    // Crear bannerType transformado si aplica
    let finalBannerType = creativityType;
    if (clientHandler.transformBannerType) {
      finalBannerType = clientHandler.transformBannerType(
        { ...formData, _creativityType: creativityType },
        creativityType
      ) as CreativityType;
    }

    // Limpiar formData si aplica
    const cleanedConfig = clientHandler.cleanFormData
      ? clientHandler.cleanFormData({ ...formData, _creativityType: creativityType }, finalBannerType)
      : (() => {
          const { title, ...config } = formData;
          return config;
        })();

    // Adjuntar templateFamily si existe
    const templateFamily = formConfig.templateFamily;

    const result = await createFormBanner(
      finalBannerType,
      cleanedConfig as AnyCreativityConfig,
      formData.title,
      templateFamily
    );

    if (result) {
      message.success('Procesando...');
      await refreshAllBanners();
      openAsset(result.bannerId);
      onNavigate('dashboard');
    } else {
      message.error('Error al crear el banner');
    }

    setIsCreating(false);
  }, [
    clientHandler,
    formData,
    createFormBanner,
    creativityType,
    onNavigate,
    refreshAllBanners,
    validateForm,
    formConfig.templateFamily,
    openAsset
  ]);

  const fields = (formConfig?.fields as any[]) || [];
  const requiredKeys = fields.filter(f => f.required).map(f => f.key);

  const isFieldFilled = (v: any) =>
    v !== undefined &&
    v !== null &&
    (typeof v !== 'string' || v.trim().length > 0) &&
    (!Array.isArray(v) || v.length > 0);

  const isFormComplete = requiredKeys.every(k => isFieldFilled(formData[k])) && Object.keys(errors).length === 0;

  return {
    formConfig,
    formData,
    errors,
    isCreating,
    isFormComplete,
    fields,
    initializeCreativityType,
    handleFieldChange,
    handleCreateBanner,
    setFieldError
  };
};
